import React, { useState, useEffect } from 'react';
import { ArrowLeft, Sparkles, Target, Leaf, BarChart3, ChevronRight, Plus, Activity, Zap } from 'lucide-react';
import { motion } from 'framer-motion';
import { compoundMultiplierContent } from '../data/compoundMultiplierContent';
import { useCalculator } from '../context/CalculatorContext';
import { Dream, DreamConfig, DreamSimulatorState, DreamProjection } from '../types/dreamSimulator';
import { formatCurrency } from '../utils/formatters';
import { predefinedDreams } from '../data/dreamSimulatorData';
import { calculateProjections } from '../utils/calculations/dreamSimulatorCalculations';
import GoalSelector from './dreamSimulator/GoalSelector';
import ImpactVisualizer from './dreamSimulator/ImpactVisualizer';
import PortfolioComparison from './dreamSimulator/PortfolioComparison';
import DreamResults from './dreamSimulator/DreamResults';
import DreamConfiguration from './dreamSimulator/DreamConfiguration';
import CreateCustomDream from './dreamSimulator/CreateCustomDream';
import CompoundInterestAnimation from './dreamSimulator/CompoundInterestAnimation';
import AnimatedCharts from './dreamSimulator/AnimatedCharts';

interface DreamSimulatorProps {
  onBack: () => void;
}

const DreamSimulator: React.FC<DreamSimulatorProps> = ({ onBack }) => {
  const { investment, updateInvestment } = useCalculator();
  
  // Initialize state
  const [state, setState] = useState<DreamSimulatorState>({
    selectedDream: null,
    dreamConfig: null,
    dreamProjections: [],
    portfolioComparison: null,
    activeView: 'selection'
  });
  
  // Add state for selected comparisons
  const [selectedComparisons, setSelectedComparisons] = useState<string[]>(['cosechaCapital', 'cetes', 'savings', 'realEstate']);

  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Scroll to top when active view changes
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [state.activeView]);

  // Handle dream selection
  const handleDreamSelect = (dream: Dream) => {
    setState(prev => ({
      ...prev,
      selectedDream: dream,
      dreamConfig: {
        id: dream.id,
        monthlyGoal: dream.monthlyGoal,
        timeframe: dream.timeline === 'short' ? 5 : 
                  dream.timeline === 'medium' ? 10 : 
                  dream.timeline === 'long' ? 20 : 25,
        certificatesNeeded: dream.certificatesNeeded,
        isCustom: dream.isCustom || false,
        reinvestProfits: investment.reinvestProfits
      },
      activeView: 'configuration'
    }));
  };

  // Handle custom dream creation
  const handleCreateCustomDream = () => {
    setState(prev => ({
      ...prev,
      activeView: 'createCustomDream'
    }));
  };

  // Handle saving custom dream
  const handleSaveCustomDream = (dreamData: { 
    name: string; 
    description: string; 
    monthlyGoal: number; 
    timeframe: number;
  }) => {
    // Generate a unique ID for the custom dream
    const customId = `custom-${Date.now()}`;
    
    // Create a new custom dream
    const customDream: Dream = {
      id: customId,
      name: dreamData.name,
      description: dreamData.description,
      category: 'custom',
      icon: Plus, // Using Plus icon as default for custom dreams
      difficulty: 'medium', // Default difficulty
      timeline: dreamData.timeframe <= 5 ? 'short' : 
               dreamData.timeframe <= 10 ? 'medium' : 
               dreamData.timeframe <= 20 ? 'long' : 'very-long',
      monthlyGoal: dreamData.monthlyGoal,
      timeframe: dreamData.timeframe,
      certificatesNeeded: Math.ceil(dreamData.monthlyGoal / 10000), // Simple initial estimate
      isCustom: true,
      impactMetrics: {
        environmentalImpact: {
          co2Reduction: 10,
          waterSaved: 25000,
          landPreserved: 4000
        },
        socialImpact: {
          jobsCreated: 2,
          familiesSupported: 4,
          communityProjects: 0.6
        },
        economicImpact: {
          localEconomyBoost: 0.4,
          taxContribution: 60000,
          sustainableGrowth: 0.7
        }
      },
      details: [
        'Sueño personalizado creado por ti',
        'Adaptado a tus necesidades específicas',
        'Flexible y ajustable según tus objetivos',
        'Respaldado por la solidez de Cosecha Capital'
      ]
    };
    
    // Select the newly created dream
    handleDreamSelect(customDream);
  };

  // Handle configuration updates
  const handleConfigUpdate = (config: Partial<DreamConfig>) => {
    setState(prev => ({
      ...prev,
      dreamConfig: {
        ...prev.dreamConfig!,
        ...config
      }
    }));
    
    // Update global investment state if reinvestProfits is changed
    if (config.reinvestProfits !== undefined) {
      updateInvestment({ reinvestProfits: config.reinvestProfits });
    }
  };

  // Navigate to a specific view
  const navigateTo = (view: DreamSimulatorState['activeView']) => {
    setState(prev => ({
      ...prev,
      activeView: view
    }));
  };

  // Calculate dream projections when config changes
  useEffect(() => {
    if (state.dreamConfig) {
      const projections = calculateProjections(state.dreamConfig, investment);
      
      setState(prev => ({
        ...prev,
        dreamProjections: projections
      }));
    }
  }, [state.dreamConfig, investment]);

  // Progress steps
  const steps = [
    { id: 'selection', label: 'Selección', icon: <Target className="w-4 h-4" /> },
    { id: 'createCustomDream', label: 'Personalizar', icon: <Plus className="w-4 h-4" /> },
    { id: 'configuration', label: 'Configuración', icon: <Sparkles className="w-4 h-4" /> },
    { id: 'compoundInterest', label: 'Interés Compuesto', icon: <Zap className="w-4 h-4" /> },
    { id: 'animations', label: 'Gráficos', icon: <Activity className="w-4 h-4" /> },
    { id: 'impact', label: 'Impacto', icon: <Leaf className="w-4 h-4" /> },
    { id: 'portfolio', label: 'Portafolio', icon: <BarChart3 className="w-4 h-4" /> }
  ];

  // Filter steps to show only relevant ones
  const filteredSteps = steps.filter(step => {
    // Only show 'createCustomDream' step when it's active
    if (step.id === 'createCustomDream' && state.activeView !== 'createCustomDream') {
      return false;
    }
    return true;
  });

  // Render the appropriate view
  const renderView = () => {
    switch (state.activeView) {
      case 'selection':
        return (
          <GoalSelector 
            dreams={predefinedDreams} 
            onDreamSelect={handleDreamSelect}
            onCreateCustomDream={handleCreateCustomDream}
          />
        );
      case 'createCustomDream':
        return (
          <CreateCustomDream
            onSave={handleSaveCustomDream}
            onCancel={() => navigateTo('selection')}
          />
        );
      case 'configuration':
        return (
          <DreamConfiguration 
            dream={state.selectedDream!}
            config={state.dreamConfig!}
            onConfigUpdate={handleConfigUpdate}
            onContinue={() => navigateTo('compoundInterest')}
            investment={investment}
          />
        );
      case 'compoundInterest':
        return (
          <CompoundInterestAnimation
            dream={state.selectedDream!}
            config={state.dreamConfig!}
            projections={state.dreamProjections || []}
            onContinue={() => navigateTo('animations')}
            investment={investment}
            selectedComparisons={selectedComparisons}
            onSelectedComparisonsChange={setSelectedComparisons}
          />
        );
      case 'animations':
        return (
          <AnimatedCharts
            dream={state.selectedDream!}
            config={state.dreamConfig!}
            projections={state.dreamProjections}
            onContinue={() => navigateTo('impact')}
            onBack={() => navigateTo('compoundInterest')}
            investment={investment}
            selectedComparisons={selectedComparisons}
            onSelectedComparisonsChange={setSelectedComparisons}
          />
        );
      case 'impact':
        return (
          <ImpactVisualizer 
            dream={state.selectedDream!}
            config={state.dreamConfig!}
            onContinue={() => navigateTo('portfolio')}
          />
        );
      case 'portfolio':
        return (
          <PortfolioComparison 
            dream={state.selectedDream!}
            config={state.dreamConfig!}
            onContinue={() => navigateTo('selection')}
          />
        );
      default:
        return <GoalSelector dreams={predefinedDreams} onDreamSelect={handleDreamSelect} onCreateCustomDream={handleCreateCustomDream} />;
    }
  };

  // Function to handle direct step navigation
  const handleStepClick = (stepId: string) => {
    // Only allow navigation if we have a selected dream (except for selection and createCustomDream)
    if (stepId === 'selection') {
      navigateTo('selection');
      return;
    }
    
    if (stepId === 'createCustomDream') {
      navigateTo('createCustomDream');
      return;
    }
    
    // For other steps, require a selected dream and configuration
    if (!state.selectedDream) {
      // If no dream selected, go to selection
      navigateTo('selection');
      return;
    }
    
    if (stepId === 'configuration') {
      navigateTo('configuration');
      return;
    }
    
    // For compoundInterest, animations, impact, and portfolio - require dream config
    if (!state.dreamConfig) {
      // If no config, go to configuration
      navigateTo('configuration');
      return;
    }
    
    // Navigate to the requested step
    navigateTo(stepId as DreamSimulatorState['activeView']);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 to-purple-50">
      {/* Header */}
      <div className="bg-white/80 backdrop-blur-sm shadow-sm border-b border-white/20">
        <div className="max-w-7xl mx-auto px-6 py-6">
          <div className="flex items-center gap-6">
            <button
              onClick={onBack}
              className="flex items-center gap-2 px-4 py-2 text-gray-600 hover:text-gray-800 hover:bg-gray-100/50 rounded-xl transition-all duration-200"
              title="Volver al menú principal"
            >
              <ArrowLeft className="w-5 h-5" />
              <span className="font-medium">Volver</span>
            </button>
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-2xl flex items-center justify-center">
                <Sparkles className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Certificado Cosecha de Vida</h1>
               <h2 className="text-lg font-semibold text-green-700">{compoundMultiplierContent.title}</h2>
               <p className="text-gray-600">{compoundMultiplierContent.tagline}</p>
              </div>
              
              {/* Disclaimer */}
              <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                <p className="text-xs text-yellow-800">
                  <strong>⚠️ Aviso:</strong> Las proyecciones mostradas son estimaciones y no garantizan rendimientos futuros.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Progress Steps */}
      <div className="bg-white border-b">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            {filteredSteps.map((step, index) => {
              const isActive = state.activeView === step.id;
              const isPast = filteredSteps.findIndex(s => s.id === state.activeView) > index;
              const isClickable = step.id === 'selection' || step.id === 'createCustomDream' || 
                                (state.selectedDream && (step.id === 'configuration' || state.dreamConfig));
              
              return (
                <div key={step.id} className="flex items-center">
                  {index > 0 && (
                    <div className={`h-0.5 w-10 mx-2 ${isPast ? 'bg-indigo-500' : 'bg-gray-200'}`}></div>
                  )}
                  <button 
                    onClick={() => handleStepClick(step.id)}
                    className={`flex flex-col items-center transition-all duration-200 ${
                      isClickable ? 'cursor-pointer hover:scale-105' : 'cursor-pointer opacity-50'
                    } ${isActive ? 'text-indigo-600' : isPast ? 'text-indigo-400' : 'text-gray-400'}`}
                    title={isClickable ? `Ir a ${step.label}` : `Completa los pasos anteriores para acceder a ${step.label}`}
                  >
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                      isActive ? 'bg-indigo-100 text-indigo-600 ring-2 ring-indigo-500' : 
                      isPast ? 'bg-indigo-50 text-indigo-500' : 'bg-gray-100'
                    }`}>
                      {step.icon}
                    </div>
                    <span className="text-xs mt-1">{step.label}</span>
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Main Content */}
      <motion.div 
        className="max-w-7xl mx-auto px-6 py-8"
        key={state.activeView}
      >
        <motion.div
          initial={{ opacity: 0, x: 50 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: -50 }}
          transition={{ 
            duration: 0.6,
            ease: [0.4, 0, 0.2, 1]
          }}
        >
          {renderView()}
        </motion.div>
      </motion.div>
    </div>
  );
};

export default DreamSimulator;